/**
 * Parses the `message` events posted by Meta's Embedded Signup popup.
 *
 * `window` receives messages from any frame on the page, so the origin is
 * checked against an exact allowlist before the payload is trusted.
 */

const TRUSTED_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];

const MESSAGE_TYPE = "WA_EMBEDDED_SIGNUP";

export type EmbeddedSignupMessage =
  | { event: "FINISH"; wabaId: string; phoneNumberId: string }
  | { event: "CANCEL"; currentStep?: string };

interface RawPayload {
  type?: unknown;
  event?: unknown;
  data?: {
    waba_id?: unknown;
    phone_number_id?: unknown;
    current_step?: unknown;
  };
}

const toPayload = (data: unknown): RawPayload | null => {
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as RawPayload;
    } catch {
      return null;
    }
  }
  if (typeof data === "object" && data !== null) return data as RawPayload;
  return null;
};

const asId = (value: unknown) =>
  typeof value === "string" && value !== "" ? value : null;

export const parseEmbeddedSignupMessage = (
  event: MessageEvent
): EmbeddedSignupMessage | null => {
  if (!TRUSTED_ORIGINS.includes(event.origin)) return null;

  const payload = toPayload(event.data);
  if (!payload || payload.type !== MESSAGE_TYPE) return null;

  const data = payload.data ?? {};

  if (payload.event === "FINISH") {
    const wabaId = asId(data.waba_id);
    const phoneNumberId = asId(data.phone_number_id);
    if (!wabaId || !phoneNumberId) return null;
    return { event: "FINISH", wabaId, phoneNumberId };
  }

  if (payload.event === "CANCEL") {
    const currentStep = asId(data.current_step);
    return currentStep ? { event: "CANCEL", currentStep } : { event: "CANCEL" };
  }

  return null;
};
